import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { NetRateFormulaState } from './types';

export type NetRateFormulaResult = {
  applyToExtraNight: boolean;
  netPercent: number;
  roomIds: Array<number>;
};

export type NetRateFormulaRoom = {
  id: number;
  name: string;
};

type NetRateFormulaDialogProps = {
  allRoomIds: Array<number>;
  formula: NetRateFormulaState;
  onApply: (result: NetRateFormulaResult) => void;
  onChangeFormula: (formula: NetRateFormulaState) => void;
  onClose: () => void;
  open: boolean;
  rooms: Array<NetRateFormulaRoom>;
};

export default function NetRateFormulaDialog({
  allRoomIds,
  formula,
  onApply,
  onChangeFormula,
  onClose,
  open,
  rooms,
}: NetRateFormulaDialogProps) {
  const { netPercent, applyToExtraNight, scope, selectedRoomIds } = formula;
  const clampedNetPercent = Math.min(100, Math.max(0, Number(netPercent || 0)));
  const marginPercent = 100 - clampedNetPercent;
  const canApply = scope === 'all' || selectedRoomIds.length > 0;

  function handleApply() {
    onApply({
      applyToExtraNight,
      netPercent: clampedNetPercent,
      roomIds: scope === 'choose' ? selectedRoomIds : allRoomIds,
    });
    onClose();
  }

  function toggleRoom(id: number) {
    onChangeFormula({
      ...formula,
      selectedRoomIds: selectedRoomIds.includes(id)
        ? selectedRoomIds.filter((roomId) => roomId !== id)
        : [...selectedRoomIds, id],
    });
  }

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
        Net Rate formula
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography color="text.secondary" variant="body2">
            Set each room type&apos;s net as a share of its sell price. Margin % updates to match.
          </Typography>

          <Box>
            <Stack alignItems="center" direction="row" spacing={1.5}>
              <Typography variant="subtitle2">Net =</Typography>
              <TextField
                inputProps={{ max: 100, min: 0, style: { textAlign: 'center' } }}
                onChange={(e) => onChangeFormula({ ...formula, netPercent: e.target.value })}
                size="small"
                sx={{ width: 90 }}
                type="number"
                value={netPercent}
              />
              <Typography variant="body2">% of sell</Typography>
            </Stack>
            <Typography color="text.secondary" mt={1} variant="body2">
              That&apos;s a {marginPercent}% margin — net is {clampedNetPercent}% of sell on every priced stay.
            </Typography>
          </Box>

          <Box>
            <Typography color="text.secondary" sx={{ display: 'block', letterSpacing: 0.8, mb: 1 }} variant="overline">
              Apply to
            </Typography>
            <RadioGroup
              onChange={(e) => onChangeFormula({ ...formula, scope: e.target.value as 'all' | 'choose' })}
              value={scope}
            >
              <FormControlLabel
                control={<Radio size="small" />}
                label="All room types"
                slotProps={{ typography: { variant: 'body2' } }}
                value="all"
              />
              <FormControlLabel
                control={<Radio size="small" />}
                label="Choose room types…"
                slotProps={{ typography: { variant: 'body2' } }}
                value="choose"
              />
            </RadioGroup>

            {scope === 'choose' && (
              <Stack sx={{ maxHeight: 180, ml: 3.5, mt: 0.5, overflowY: 'auto' }}>
                {rooms.length === 0 ? (
                  <Typography color="text.secondary" fontStyle="italic" variant="caption">
                    No priced room types yet
                  </Typography>
                ) : (
                  rooms.map((room) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedRoomIds.includes(room.id)}
                          onChange={() => toggleRoom(room.id)}
                          size="small"
                        />
                      }
                      key={room.id}
                      label={room.name}
                      slotProps={{ typography: { variant: 'body2' } }}
                    />
                  ))
                )}
              </Stack>
            )}
          </Box>

          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToExtraNight}
                  onChange={(e) => onChangeFormula({ ...formula, applyToExtraNight: e.target.checked })}
                />
              }
              label="Also apply to extra-night net"
              slotProps={{ typography: { variant: 'body2' } }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button color="primary" disabled={!canApply} onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
